export interface GuideStep {
  title: string;
  detail: string;
}

export interface GuideSection {
  id: string;
  title: string;
  icon: string;
  description: string;
  steps: GuideStep[];
  actionLabel?: string;
  actionRoute?: string;
}

export const guideSections: GuideSection[] = [
  {
    id: 'demarrer-consultation',
    title: 'Démarrer une consultation',
    icon: 'ri-stethoscope-line',
    description: 'Il y a deux façons de lancer une consultation, selon que le patient a un rendez-vous ou non.',
    steps: [
      {
        title: 'Depuis un rendez-vous',
        detail: 'Sur « Mon Agenda », repérez le rendez-vous marqué « En attente » et cliquez sur le bouton « Démarrer ». La consultation s\'ouvre immédiatement pour ce patient.',
      },
      {
        title: 'Sans rendez-vous (consultation directe)',
        detail: 'Cliquez sur « Nouvelle consultation » en haut de l\'agenda, puis recherchez le patient par son nom, prénom ou n° de dossier.',
      },
      {
        title: 'Reprendre une consultation en pause',
        detail: 'Si une consultation est déjà « En cours », le bouton devient « Reprendre ». Un clic et vous retrouvez exactement où vous en étiez.',
      },
    ],
  },
  {
    id: 'dossier-patient',
    title: 'Gérer le dossier d\'un patient',
    icon: 'ri-folder-user-line',
    description: 'Le dossier médical centralise toutes les informations utiles du patient.',
    steps: [
      {
        title: 'Ouvrir le dossier',
        detail: 'Cliquez sur le nom d\'un patient, soit dans l\'agenda, soit dans la liste « Patients de la clinique ». Son dossier s\'ouvre.',
      },
      {
        title: 'Consulter les informations',
        detail: 'Retrouvez les informations personnelles, médicales, les antécédents (maladies chroniques, chirurgicaux) et les médicaments actuels.',
      },
      {
        title: 'Modifier le dossier',
        detail: 'Cliquez sur « Modifier le dossier », mettez à jour les champs, puis « Enregistrer les modifications ».',
      },
      {
        title: 'Consulter l\'historique',
        detail: 'L\'onglet « Historique » liste toutes les consultations, prescriptions et analyses du patient. Chaque consultation a son propre bouton « Fiche de visite ».',
      },
    ],
  },
  {
    id: 'prescription',
    title: 'Créer une prescription / ordonnance',
    icon: 'ri-file-list-3-line',
    description: 'Rédigez une ordonnance directement pendant ou après la consultation.',
    steps: [
      {
        title: 'Ouvrir l\'éditeur',
        detail: 'Depuis la consultation en cours, cliquez sur le bouton de prescription. Vous arrivez sur l\'écran de création.',
      },
      {
        title: 'Ajouter les médicaments',
        detail: 'Recherchez chaque médicament (la saisie est insensible aux accents et à la casse) et ajoutez la posologie, la voie et la durée.',
      },
      {
        title: 'Valider et imprimer',
        detail: 'Enregistrez la prescription puis imprimez-la pour la remettre au patient. Elle reste visible dans l\'historique du dossier.',
      },
    ],
  },
  {
    id: 'bon-laboratoire',
    title: 'Créer un bon de laboratoire',
    icon: 'ri-flask-line',
    description: 'Prescrivez des analyses et suivez leurs résultats.',
    steps: [
      {
        title: 'Ouvrir un bon',
        detail: 'Depuis la consultation ou le dossier patient, cliquez sur « Bon de laboratoire ».',
      },
      {
        title: 'Sélectionner les analyses',
        detail: 'Choisissez les analyses à réaliser (NFS, biochimie, sérologie, etc.) pour ce patient.',
      },
      {
        title: 'Suivre les résultats',
        detail: 'Une fois les analyses saisies par le laborantin, les résultats et conclusions apparaissent dans le dossier du patient.',
      },
    ],
  },
  {
    id: 'suite-soins',
    title: 'Suivre une suite de soins',
    icon: 'ri-heart-pulse-line',
    description: 'Après une consultation, assurez le suivi du patient hospitalisé jusqu\'à sa libération.',
    steps: [
      {
        title: 'La suite se crée automatiquement',
        detail: 'À la fin d\'une consultation, une suite de soins est créée automatiquement (sauf si vous désactivez l\'option dans l\'écran « Fin de consultation »).',
      },
      {
        title: 'Ajouter des plaintes et des soins',
        detail: 'Dans la suite, ajoutez les nouvelles plaintes, puis les sessions de soins administrées, datées et détaillées.',
      },
      {
        title: 'Faire la relève',
        detail: 'Avant de partir, rédigez un compte rendu de relève pour informer le médecin suivant. À la sortie, créez l\'ordonnance de sortie.',
      },
    ],
  },
  {
    id: 'fiche-visite',
    title: 'Générer une fiche de visite',
    icon: 'ri-clipboard-line',
    description: 'Une fiche de tournée imprimable pour suivre les patients hospitalisés de salle en salle.',
    steps: [
      {
        title: 'Ouvrir depuis le dossier',
        detail: 'Dans le dossier d\'un patient, cliquez sur « Fiche de visite ». Choisissez l\'hospitalisation concernée si plusieurs existent.',
      },
      {
        title: 'Consulter la fiche',
        detail: 'La fiche regroupe identité, antécédents, plaintes, soins, bilan labo et diagnostic de cette hospitalisation, avec un badge de statut.',
      },
      {
        title: 'Imprimer',
        detail: 'Cliquez sur « Imprimer la fiche » pour obtenir un document papier propre, parfait pour vos tournées en salle.',
      },
    ],
  },
  {
    id: 'disponibilites',
    title: 'Gérer mes disponibilités',
    icon: 'ri-time-line',
    description: 'Définissez vos créneaux pour permettre la prise de rendez-vous.',
    steps: [
      {
        title: 'Ouvrir mes disponibilités',
        detail: 'Depuis le menu de votre profil (en haut à droite), cliquez sur « Mes disponibilités ».',
      },
      {
        title: 'Définir les créneaux',
        detail: 'Ajoutez vos horaires et jours de consultation. Les patients et secrétaires verront ces créneaux pour réserver.',
      },
      {
        title: 'Mettre à jour à tout moment',
        detail: 'Modifiez ou retirez des créneaux librement. Les changements sont pris en compte immédiatement.',
      },
    ],
    actionLabel: 'Gérer mes disponibilités',
    actionRoute: '/medecin/disponibilites',
  },
];